import { Alert, Box, Button } from "@mui/material";
import { useEffect, useState } from "react";
import { acknowledgeCeleryErrorsByTaskName } from "../api/celtask";
import {
	DELETION_REQUESTS_CHANGED,
	fetchPendingDeletionRequests,
	type PendingDeletionRequests,
} from "../api/deletion-requests";
import { acknowledgePluginWorkLogBucket } from "../api/plugin-work-log";
import {
	type CeleryErrorsHealth,
	type CookieMismatchHealth,
	type DbBackupHealth,
	type EsIndexHealth,
	type ExtractionHealth,
	type ExtractionHealthCheck,
	type ExtractionQualityCheck,
	fetchCeleryErrorsHealth,
	fetchCookieMismatchHealth,
	fetchDbBackupHealth,
	fetchEmailQueueHealth,
	fetchEsIndexHealth,
	fetchExtractionHealth,
	fetchGpuBoxHealth,
	fetchJobylonIngestHealth,
	fetchLinkedinQueueHealth,
	fetchPluginWorkLogHealth,
	type GpuBoxHealth,
	type JobylonIngestHealth,
	type JobylonJobDocuments,
	type MemberIdentity,
	type PluginWorkLogHealth,
	type QueueFlowHealth,
} from "../api/queue-health";
import {
	formatDateTime,
	formatDurationShort,
	formatRelativeTimeShort,
} from "../utils/date-formatting";
import { SnoozeButton, useIsBannerSnoozed } from "./banner-snooze";
import { useGlobalState } from "./globalState";
import { canOpenPath } from "./navbar/route-access";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const formatExtractionFailure = (c: ExtractionHealthCheck): string => {
	const parts: string[] = [];
	if (c.alert_1h) parts.push(`${c.value_1h}/h < ${c.floor_1h}`);
	if (c.alert_24h) parts.push(`${c.value_24h}/24h < ${c.floor_24h}`);
	return `${c.metric}: ${parts.join(", ")}`;
};

const formatQualityFailure = (q: ExtractionQualityCheck): string =>
	`${q.label}: ${q.pct_24h}% missing > ${q.ceiling_pct}%`;

const describeMember = (member: MemberIdentity): string =>
	member.name ? `${member.name} (${member.member_id})` : `member ${member.member_id}`;

const formatHours = (hours: number | null): string =>
	hours === null ? "unknown" : formatDurationShort(hours * 3600);

/** Why the queue is unhealthy, in the terms the check actually measured. */
const describeQueueFlow = (q: QueueFlowHealth): string => {
	if (q.stalled) {
		const last = q.last_sent_at
			? `last send ${formatRelativeTimeShort(q.last_sent_at)} ago`
			: "never sent";
		return `${q.backlog} waiting and nothing sent in 24h (${last}).`;
	}
	// Count the rows past the cutoff, not the whole backlog: the rest are
	// legitimately in flight, and Review shows only these.
	const verb = q.stuck === 1 ? "has" : "have";
	return (
		`${q.stuck} of ${q.backlog} waiting ${verb} outlived what throughput ` +
		`explains — oldest queued ${formatRelativeTimeShort(q.oldest_backlog_at)} ago, ` +
		`yet ${q.sent_window} sent in ${q.window_days}d clears the whole backlog ` +
		`in ${formatHours(q.drain_hours)}.`
	);
};

/** A queue's snooze key carries its state, so muting a slow backlog does not
 * also mute the sender dying outright a few hours later. */
const queueKey = (slug: string, h: QueueFlowHealth): string =>
	`${slug}-queue:${h.stalled ? "stalled" : "aging"}`;

/**
 * A "Review" link to a screen the viewer may not open is left out: it would
 * only bounce them to /todo (staff see these banners, superuser screens
 * are not all theirs).
 */
const ReviewLink: React.FC<{ href: string; children: React.ReactNode }> = ({
	href,
	children,
}) => {
	const { currentUser } = useGlobalState();
	if (!canOpenPath(href.split("?")[0], currentUser)) return null;
	return <a href={href}>{children}</a>;
};

interface QueueFlowAlertProps {
	label: string;
	health: QueueFlowHealth;
	/** Queues tab, already scoped to this channel. */
	queueHref: string;
	slug: string;
}

/** Flow alert for one queue: it has stopped moving, or a row is being skipped. */
const QueueFlowAlert: React.FC<QueueFlowAlertProps> = ({
	label,
	health,
	queueHref,
	slug,
}) =>
	health.alert ? (
		<Alert
			severity={health.stalled ? "error" : "warning"}
			data-component={`${slug}-queue-flow-banner`}
			action={<SnoozeButton bannerKey={queueKey(slug, health)} />}
		>
			{label} queue {health.stalled ? "stalled" : "backlog aging"}:{" "}
			{describeQueueFlow(health)} <a href={`${queueHref}&stuck=true`}>Review</a>
		</Alert>
	) : null;

/** Applications without their CV, summed per customer, worst first. */
const blindApplicationsByCustomer = (
	jobs: JobylonJobDocuments[]
): [string, number][] => {
	const totals = new Map<string, number>();
	for (const job of jobs) {
		if (job.without_documents === 0) continue;
		const customer = job.company ?? "A customer without a feed";
		totals.set(customer, (totals.get(customer) ?? 0) + job.without_documents);
	}
	return [...totals].sort((a, b) => b[1] - a[1]);
};

const formatBackupAge = (ageHours: number | null): string => {
	if (ageHours === null) return "no backup found";
	if (ageHours >= 48) return `${Math.floor(ageHours / 24)}d ago`;
	return `${Math.round(ageHours)}h ago`;
};

export const QueueHealthBanner: React.FC = () => {
	const { currentUser } = useGlobalState();
	const isStaff = !!(currentUser?.is_staff || currentUser?.is_superuser);
	const isAdmin = !!currentUser?.is_superuser;
	const [email, setEmail] = useState<QueueFlowHealth | null>(null);
	const [linkedin, setLinkedin] = useState<QueueFlowHealth | null>(null);
	const [extraction, setExtraction] = useState<ExtractionHealth | null>(null);
	const [celeryErrors, setCeleryErrors] = useState<CeleryErrorsHealth | null>(null);
	const [pluginWorkLog, setPluginWorkLog] = useState<PluginWorkLogHealth | null>(
		null
	);
	const [dbBackup, setDbBackup] = useState<DbBackupHealth | null>(null);
	const [gpuBox, setGpuBox] = useState<GpuBoxHealth | null>(null);
	const [esIndex, setEsIndex] = useState<EsIndexHealth | null>(null);
	const [cookieMismatch, setCookieMismatch] = useState<CookieMismatchHealth | null>(
		null
	);
	const [jobylon, setJobylon] = useState<JobylonIngestHealth | null>(null);
	const [deletionRequests, setDeletionRequests] =
		useState<PendingDeletionRequests | null>(null);
	const isSnoozed = useIsBannerSnoozed();

	useEffect(() => {
		if (!isStaff) return;
		let cancelled = false;
		const load = async () => {
			const [e, l, x, c, p, b, es, m, j, d, g] = await Promise.all([
				fetchEmailQueueHealth().catch(() => null),
				fetchLinkedinQueueHealth().catch(() => null),
				fetchExtractionHealth().catch(() => null),
				fetchCeleryErrorsHealth().catch(() => null),
				fetchPluginWorkLogHealth().catch(() => null),
				fetchDbBackupHealth().catch(() => null),
				fetchEsIndexHealth().catch(() => null),
				fetchCookieMismatchHealth().catch(() => null),
				fetchJobylonIngestHealth().catch(() => null),
				fetchPendingDeletionRequests().catch(() => null),
				fetchGpuBoxHealth().catch(() => null),
			]);
			if (cancelled) return;
			setEmail(e);
			setLinkedin(l);
			setExtraction(x);
			setCeleryErrors(c);
			setPluginWorkLog(p);
			setDbBackup(b);
			setEsIndex(es);
			setCookieMismatch(m);
			setJobylon(j);
			setDeletionRequests(d);
			setGpuBox(g);
		};
		load();
		const id = setInterval(load, POLL_INTERVAL_MS);

		// Refresh the celery-errors list when a user acknowledges a failure on
		// the celery_tasks page or via this banner — the 5-minute poll is too
		// slow to feel responsive.
		const refreshCeleryErrors = async () => {
			const c = await fetchCeleryErrorsHealth().catch(() => null);
			if (!cancelled) setCeleryErrors(c);
		};
		window.addEventListener("celery-errors-changed", refreshCeleryErrors);

		const refreshPluginWorkLog = async () => {
			const p = await fetchPluginWorkLogHealth().catch(() => null);
			if (!cancelled) setPluginWorkLog(p);
		};
		window.addEventListener("plugin-work-log-changed", refreshPluginWorkLog);

		const refreshDeletionRequests = async () => {
			const d = await fetchPendingDeletionRequests().catch(() => null);
			if (!cancelled) setDeletionRequests(d);
		};
		window.addEventListener(DELETION_REQUESTS_CHANGED, refreshDeletionRequests);

		return () => {
			cancelled = true;
			clearInterval(id);
			window.removeEventListener("celery-errors-changed", refreshCeleryErrors);
			window.removeEventListener("plugin-work-log-changed", refreshPluginWorkLog);
			window.removeEventListener(
				DELETION_REQUESTS_CHANGED,
				refreshDeletionRequests
			);
		};
	}, [isStaff]);

	// Keys that carry what the alert names come back when that changes: another
	// failing metric, another failing task, another missing index or request.
	const failingChecks = extraction?.checks.filter((c) => c.alert) ?? [];
	const failingQuality = extraction?.quality.filter((q) => q.alert) ?? [];
	// Sorted: the server orders groups by count, which reshuffles without news.
	const extractionKey = `extraction:${[...failingChecks, ...failingQuality]
		.map((c) => c.metric)
		.sort()
		.join(",")}`;
	const celeryErrorsKey = `celery-errors:${(celeryErrors?.groups ?? [])
		.map((g) => g.task_name)
		.sort()
		.join(",")}`;
	const esIndexKey = `es-index:${[...(esIndex?.missing ?? [])].sort().join(",")}`;
	const deletionRequestsKey = `deletion-requests:${deletionRequests?.count}`;

	const emailUnhealthy = !!email?.alert && !isSnoozed(queueKey("email", email));
	const linkedinUnhealthy =
		!!linkedin?.alert && !isSnoozed(queueKey("linkedin", linkedin));
	const extractionBroken =
		isAdmin &&
		failingChecks.length + failingQuality.length > 0 &&
		!isSnoozed(extractionKey);
	const celeryErrorsOver =
		celeryErrors &&
		celeryErrors.unsent > celeryErrors.threshold &&
		!isSnoozed(celeryErrorsKey);
	const pluginWorkLogOver =
		pluginWorkLog &&
		(pluginWorkLog.errors > pluginWorkLog.threshold ||
			pluginWorkLog.stuck > pluginWorkLog.threshold) &&
		!isSnoozed("plugin-work-log");
	const dbBackupStale = dbBackup?.alert && !isSnoozed("db-backup");
	const gpuBoxMissing = !!gpuBox?.alert && !isSnoozed("gpu-box");
	const esIndexMissing = esIndex?.alert && !isSnoozed(esIndexKey);
	// Review links to /charts/cookies, whose API is superuser-only.
	const cookieMismatched =
		isAdmin &&
		cookieMismatch &&
		cookieMismatch.count > 0 &&
		!isSnoozed("cookie-mismatch");
	const jobylonBacklogged = !!jobylon?.alert && !isSnoozed("jobylon");
	const jobylonPartnerActionMissing =
		isAdmin &&
		!!jobylon?.partner_action_alert &&
		!isSnoozed("jobylon-partner-action");
	const deletionRequestsWaiting =
		!!deletionRequests &&
		deletionRequests.count > 0 &&
		!isSnoozed(deletionRequestsKey);

	if (
		!deletionRequestsWaiting &&
		!emailUnhealthy &&
		!linkedinUnhealthy &&
		!extractionBroken &&
		!celeryErrorsOver &&
		!pluginWorkLogOver &&
		!dbBackupStale &&
		!gpuBoxMissing &&
		!esIndexMissing &&
		!cookieMismatched &&
		!jobylonBacklogged &&
		!jobylonPartnerActionMissing
	)
		return null;

	const ackGroup = async (group: CeleryErrorsHealth["groups"][number]) => {
		// Gone on the click, not after the round trip: the click is the decision,
		// and the re-fetch below settles the count either way.
		setCeleryErrors((current) =>
			current
				? {
						...current,
						unsent: current.unsent - group.count,
						groups: current.groups.filter(
							(g) => g.task_name !== group.task_name
						),
					}
				: current
		);
		await acknowledgeCeleryErrorsByTaskName(group.task_name).catch(() => null);
		window.dispatchEvent(new Event("celery-errors-changed"));
	};

	const ackPluginBucket = async (bucket: "errors" | "stuck") => {
		await acknowledgePluginWorkLogBucket(bucket).catch(() => null);
		window.dispatchEvent(new Event("plugin-work-log-changed"));
	};

	return (
		<>
			{deletionRequestsWaiting && deletionRequests && (
				<Alert
					severity="warning"
					data-component="deletion-requests-banner"
					action={<SnoozeButton bannerKey={deletionRequestsKey} />}
				>
					Data deletion: {deletionRequests.count}{" "}
					{deletionRequests.count === 1 ? "request awaits" : "requests await"}{" "}
					review
					{deletionRequests.oldest_confirmed_at &&
						`, oldest confirmed ${formatRelativeTimeShort(deletionRequests.oldest_confirmed_at)} ago`}
					. <a href="/user-management/deletion-requests">Review</a>
				</Alert>
			)}
			{cookieMismatched && (
				<Alert
					severity="error"
					data-component="cookie-mismatch-banner"
					action={<SnoozeButton bannerKey="cookie-mismatch" />}
				>
					<Box sx={{ mb: 0.5 }}>
						LinkedIn account mismatch: {cookieMismatch.count}{" "}
						{cookieMismatch.count === 1 ? "session holds" : "sessions hold"}{" "}
						cookies for a different member than the user is registered with
						— messages sent through{" "}
						{cookieMismatch.count === 1 ? "it" : "them"} go out from the
						wrong account. <a href="/charts/cookies?tab=cookies">Review</a>
					</Box>
					<Box
						component="ul"
						sx={{ m: 0, pl: 2, listStyle: "disc" }}
						data-component="cookie-mismatch-list"
					>
						{cookieMismatch.mismatches.map((m) => (
							<li key={m.session_id}>
								<Box component="span" sx={{ fontSize: "0.85em" }}>
									<Box component="code" sx={{ fontWeight: 600 }}>
										{m.username}
									</Box>{" "}
									(bot {m.bot_id}) — registered as{" "}
									{describeMember(m.expected)}, cookies belong to{" "}
									{describeMember(m.li_at)}
								</Box>
							</li>
						))}
					</Box>
				</Alert>
			)}
			{/* Jobylon: a stored webhook event that never became an ODI record is an
			    application nobody can see. Until this existed the only way to notice
			    was to scroll the raw Celery task list, which is how the 11 400-event
			    backlog of 2026-09-18 was eventually found. */}
			{jobylonBacklogged && jobylon && (
				<Alert
					severity="error"
					data-component="jobylon-ingest-banner"
					action={<SnoozeButton bannerKey="jobylon" />}
				>
					Jobylon ingest backlog: {jobylon.backlog.toLocaleString()} webhook
					events unprocessed, oldest from{" "}
					{jobylon.oldest_unprocessed
						? formatDateTime(jobylon.oldest_unprocessed)
						: "—"}
					. {jobylon.awaiting_file_refetch.toLocaleString()} are waiting on
					re-fetched files, {jobylon.exhausted} gave up.{" "}
					<ReviewLink href="/charts/jobylon_status">Review</ReviewLink>
				</Alert>
			)}
			{/* Jobylon sends an applicant's CV only on the Partner event, and only
			    for jobs with the "Send to Partner" action. Without it the fallback
			    queues them after JOBYLON_DOCUMENTS_WAIT_MINUTES and the verdict is
			    "no match, no evidence" — Eltel, 2026-09-25..26, noticed by nobody. */}
			{jobylonPartnerActionMissing && jobylon && (
				<Alert
					severity="warning"
					data-component="jobylon-partner-action-banner"
					action={<SnoozeButton bannerKey="jobylon-partner-action" />}
				>
					{blindApplicationsByCustomer(jobylon.job_documents).map(
						([customer, count]) => (
							<Box
								key={customer}
								component="span"
								sx={{ display: "block" }}
							>
								Jobylon: {count}{" "}
								{count === 1 ? "application" : "applications"} to{" "}
								{customer} in the last {jobylon.documents_window_hours}{" "}
								h arrived without their CV. {customer} must add the
								"Send to Partner" action to its jobs and job templates.
							</Box>
						)
					)}
					<ReviewLink href="/charts/jobylon_status">Review</ReviewLink>
				</Alert>
			)}
			{/* The same snooze-aware conditions as the early return above. Gated on
			    `email` alone, a snoozed backlog stayed hidden only while nothing else
			    was wrong, and came back the moment any other alert opened the banner. */}
			{emailUnhealthy && email && (
				<QueueFlowAlert
					label="Email"
					slug="email"
					health={email}
					queueHref="/charts/insights?tab=queues&type=email"
				/>
			)}
			{linkedinUnhealthy && linkedin && (
				<QueueFlowAlert
					label="LinkedIn"
					slug="linkedin"
					health={linkedin}
					queueHref="/charts/insights?tab=queues&type=linkedin"
				/>
			)}
			{extractionBroken && (
				<Alert
					severity="error"
					data-component="extraction-health-banner"
					action={<SnoozeButton bannerKey={extractionKey} />}
				>
					Datamining extraction unhealthy:{" "}
					{[
						...failingChecks.map(formatExtractionFailure),
						...failingQuality.map(formatQualityFailure),
					].join(" · ")}
				</Alert>
			)}
			{dbBackupStale && dbBackup && (
				<Alert
					severity="error"
					data-component="db-backup-health-banner"
					action={<SnoozeButton bannerKey="db-backup" />}
				>
					Database backup stale: last backup{" "}
					{formatBackupAge(dbBackup.age_hours)} (threshold{" "}
					{dbBackup.threshold_hours}h).{" "}
					<ReviewLink href="/charts/db_backups">Review</ReviewLink>
				</Alert>
			)}
			{gpuBoxMissing && gpuBox && (
				<Alert
					severity="warning"
					data-component="gpu-box-health-banner"
					action={<SnoozeButton bannerKey="gpu-box" />}
				>
					No GPU box heartbeat:{" "}
					{gpuBox.last_seen_seconds === null
						? "no box has registered"
						: `last heartbeat ${formatDurationShort(gpuBox.last_seen_seconds)} ago`}
					. Until a box registers, the reranker is unavailable (draws are
					judged unranked) and second-judge verdicts wait in the backlog.{" "}
					<a href="https://cloud.vast.ai/instances/">Vast instances</a>
				</Alert>
			)}
			{esIndexMissing && esIndex && (
				<Alert
					severity="error"
					data-component="es-index-health-banner"
					action={<SnoozeButton bannerKey={esIndexKey} />}
				>
					Elasticsearch index missing: {esIndex.missing.join(", ")}. Search,
					location lookups and grading queues fail until it is created.{" "}
					<ReviewLink href="/charts/elasticsearch_status">Review</ReviewLink>
				</Alert>
			)}
			{pluginWorkLogOver && (
				<Alert
					severity="error"
					data-component="plugin-work-log-health-banner"
					action={<SnoozeButton bannerKey="plugin-work-log" />}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							flexWrap: "wrap",
						}}
					>
						Artemis Helper (24h):
						{pluginWorkLog.errors > 0 && (
							<>
								<Box component="span">
									{pluginWorkLog.errors} HTTP{" "}
									{pluginWorkLog.errors === 1 ? "error" : "errors"} ·{" "}
									<ReviewLink href="/charts/plugin_work_log?errors_only=true">
										Review
									</ReviewLink>
								</Box>
								<Button
									size="small"
									variant="outlined"
									color="inherit"
									onClick={() => ackPluginBucket("errors")}
									sx={{
										fontSize: "0.7rem",
										minWidth: "auto",
										py: 0,
										px: 1,
									}}
									data-component="plugin-work-log-ack-errors-button"
								>
									Ack {pluginWorkLog.errors}
								</Button>
							</>
						)}
						{pluginWorkLog.errors > 0 && pluginWorkLog.stuck > 0 && (
							<Box component="span">·</Box>
						)}
						{pluginWorkLog.stuck > 0 && (
							<>
								<Box component="span">
									{pluginWorkLog.stuck} stuck (no reply 1h+) ·{" "}
									<ReviewLink href="/charts/plugin_work_log?stuck_only=true">
										Review
									</ReviewLink>
								</Box>
								<Button
									size="small"
									variant="outlined"
									color="inherit"
									onClick={() => ackPluginBucket("stuck")}
									sx={{
										fontSize: "0.7rem",
										minWidth: "auto",
										py: 0,
										px: 1,
									}}
									data-component="plugin-work-log-ack-stuck-button"
								>
									Ack {pluginWorkLog.stuck}
								</Button>
							</>
						)}
					</Box>
				</Alert>
			)}
			{celeryErrorsOver && (
				<Alert
					severity="error"
					data-component="celery-errors-banner"
					action={<SnoozeButton bannerKey={celeryErrorsKey} />}
				>
					<Box sx={{ mb: 0.5 }}>
						Celery: {celeryErrors.unsent} unacknowledged failed{" "}
						{celeryErrors.unsent === 1 ? "task" : "tasks"}.{" "}
						<ReviewLink href="/charts/celery_tasks?updateTimeInSeconds=10&state=FAILURE">
							Review
						</ReviewLink>
					</Box>
					<Box
						component="ul"
						sx={{ m: 0, pl: 2, listStyle: "disc" }}
						data-component="celery-errors-list"
					>
						{celeryErrors.groups.map((g) => (
							<li key={g.task_name || "(unknown)"}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										flexWrap: "wrap",
									}}
								>
									<Box
										component="code"
										sx={{ fontSize: "0.85em", fontWeight: 600 }}
									>
										{g.task_name || "(unknown task)"}
									</Box>
									<Box
										component="span"
										sx={{ fontSize: "0.85em", opacity: 0.85 }}
									>
										×{g.count} — {g.exception}
									</Box>
									<Button
										size="small"
										variant="outlined"
										color="inherit"
										onClick={() => ackGroup(g)}
										sx={{
											fontSize: "0.7rem",
											minWidth: "auto",
											py: 0,
											px: 1,
										}}
										data-component="celery-error-ack-group-button"
									>
										Ack {g.count}
									</Button>
								</Box>
							</li>
						))}
					</Box>
				</Alert>
			)}
		</>
	);
};
